import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  BarChart3,
  Users,
  Gift,
  RefreshCw,
  Download,
  Shield
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const PricingControls = () => {
  const [activeTab, setActiveTab] = useState('coupons');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Coupon Management State
  const [coupons, setCoupons] = useState([]);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    max_uses: '',
    valid_until: '',
    applicable_plans: []
  });

  // Price Change State
  const [priceRequests, setPriceRequests] = useState([]);
  const [showPriceValidation, setShowPriceValidation] = useState(false);
  const [priceValidation, setPriceValidation] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (activeTab === 'coupons') {
        await fetchCoupons();
      } else if (activeTab === 'pricing') {
        await fetchPriceRequests();
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoupons = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/monetization/coupons`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setCoupons(data.coupons || []);
    } else {
      throw new Error('Failed to fetch coupons');
    }
  };

  const fetchPriceRequests = async () => {
    const response = await fetch(`${BACKEND_URL}/api/admin/monetization/pricing/change-requests`, {
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      setPriceRequests(data.requests || []);
    } else {
      throw new Error('Failed to fetch price requests');
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/coupons`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...couponForm,
          discount_value: parseFloat(couponForm.discount_value),
          max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
          valid_until: couponForm.valid_until || null
        })
      });

      if (response.ok) {
        setShowCreateCoupon(false);
        setCouponForm({
          code: '',
          name: '',
          description: '',
          discount_type: 'percentage',
          discount_value: '',
          max_uses: '',
          valid_until: '',
          applicable_plans: []
        });
        fetchCoupons();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to create coupon');
      }
    } catch (err) {
      setError('Failed to create coupon');
    }
  };

  const handleBulkCreateCoupons = async (count) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/coupons/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          coupon: couponForm,
          count: count
        })
      });

      if (response.ok) {
        setShowBulkCreate(false);
        fetchCoupons();
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to create bulk coupons');
      }
    } catch (err) {
      setError('Failed to create bulk coupons');
    }
  };

  const validatePriceChange = async (planId, currentPricing, newPricing) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/pricing/validate-change`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_id: planId,
          current_pricing: currentPricing,
          new_pricing: newPricing
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPriceValidation(data.validation);
        setShowPriceValidation(true);
      } else {
        const error = await response.json();
        setError(error.detail || 'Failed to validate price change');
      }
    } catch (err) {
      setError('Failed to validate price change');
    }
  };

  const CouponManagementTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Coupon Management</h3>
          <p className="text-gray-600 dark:text-gray-300">Create and manage discount coupons</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowBulkCreate(true)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            <Gift className="w-4 h-4 mr-2 inline" />
            Bulk Create
          </button>
          <button 
            onClick={() => setShowCreateCoupon(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 mr-2 inline" />
            Create Coupon
          </button>
        </div>
      </div>

      {/* Coupon Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Coupons', value: coupons.length, icon: Gift, color: 'text-blue-600' },
          { label: 'Active Coupons', value: coupons.filter(c => c.is_active).length, icon: CheckCircle, color: 'text-green-600' },
          { label: 'Total Uses', value: coupons.reduce((sum, c) => sum + (c.used_count || 0), 0), icon: Users, color: 'text-purple-600' },
          { label: 'Revenue Impact', value: '$0', icon: DollarSign, color: 'text-orange-600' }
        ].map((stat, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center">
              <stat.icon className={`w-5 h-5 ${stat.color} mr-3`} />
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stat.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Coupons Table */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.map(coupon => (
              <TableRow key={coupon.id}>
                <TableCell>
                  <span className="font-mono text-sm font-medium">{coupon.code}</span>
                </TableCell>
                <TableCell>{coupon.name}</TableCell>
                <TableCell>
                  {coupon.discount_type === 'percentage' ? 
                    `${coupon.discount_value}%` : 
                    `$${coupon.discount_value}`
                  }
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div>{coupon.used_count || 0} uses</div>
                    {coupon.max_uses && (
                      <div className="text-gray-500">
                        / {coupon.max_uses} max
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={coupon.is_active ? "success" : "secondary"}>
                    {coupon.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  {coupon.valid_until ? 
                    new Date(coupon.valid_until).toLocaleDateString() :
                    'Never'
                  }
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button className="p-1 text-gray-500 hover:text-blue-600">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-yellow-600">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const PriceGuardrailsTab = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Price Guardrails</h3>
          <p className="text-gray-600 dark:text-gray-300">Monitor and control pricing changes</p>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700">
            <Download className="w-4 h-4 mr-2 inline" />
            Export Report
          </button>
        </div>
      </div>

      {/* Guardrails Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: 'Max Monthly Increase', value: '20%', status: 'active', icon: TrendingUp, color: 'text-green-600' },
          { label: 'Pending Requests', value: priceRequests.filter(r => r.status === 'pending').length, status: 'warning', icon: Clock, color: 'text-yellow-600' },
          { label: 'Changes This Month', value: '0', status: 'info', icon: BarChart3, color: 'text-blue-600' }
        ].map((item, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 dark:bg-gray-900 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{item.value}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{item.label}</div>
              </div>
              <item.icon className={`w-6 h-6 ${item.color}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Price Change Requests */}
      <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">Price Change Requests</h4>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plan</TableHead>
              <TableHead>Change</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {priceRequests.map(request => (
              <TableRow key={request.id}>
                <TableCell className="font-medium">{request.plan_name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`w-4 h-4 ${request.change_percentage > 0 ? 'text-red-500' : 'text-green-500'}`} />
                    <span className={request.change_percentage > 0.2 ? 'text-red-600 font-medium' : ''}>
                      {(request.change_percentage * 100).toFixed(1)}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>{request.change_reason}</TableCell>
                <TableCell>{request.requested_by}</TableCell>
                <TableCell>
                  <Badge variant={
                    request.status === 'approved' ? 'success' :
                    request.status === 'rejected' ? 'error' : 'warning'
                  }>
                    {request.status}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(request.requested_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  {request.status === 'pending' && (
                    <div className="flex gap-2">
                      <button className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">
                        Approve
                      </button>
                      <button className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">
                        Reject
                      </button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  // Create Coupon Modal
  const CreateCouponModal = () => (
    showCreateCoupon && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Create New Coupon
          </h3>
          
          <form onSubmit={handleCreateCoupon} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Coupon Code
              </label>
              <input
                type="text"
                value={couponForm.code}
                onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="WELCOME20"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={couponForm.name}
                onChange={(e) => setCouponForm({...couponForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="Welcome Discount"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={couponForm.description}
                onChange={(e) => setCouponForm({...couponForm, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                rows={2}
                placeholder="20% off for new customers"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Discount Type
                </label>
                <select
                  value={couponForm.discount_type}
                  onChange={(e) => setCouponForm({...couponForm, discount_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed_amount">Fixed Amount</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Discount Value
                </label>
                <input
                  type="number"
                  value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({...couponForm, discount_value: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  placeholder={couponForm.discount_type === 'percentage' ? '20' : '5.00'}
                  step={couponForm.discount_type === 'percentage' ? '1' : '0.01'}
                  required
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Uses
                </label>
                <input
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({...couponForm, max_uses: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                  placeholder="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expires
                </label>
                <input
                  type="date"
                  value={couponForm.valid_until}
                  onChange={(e) => setCouponForm({...couponForm, valid_until: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateCoupon(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700"
              >
                Create Coupon
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  );

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading pricing controls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'coupons', label: 'Coupon Management', icon: Gift },
            { key: 'pricing', label: 'Price Guardrails', icon: Shield },
            { key: 'analytics', label: 'Pricing Analytics', icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'coupons' && <CouponManagementTab />}
      {activeTab === 'pricing' && <PriceGuardrailsTab />}
      {activeTab === 'analytics' && (
        <div className="text-center py-12">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Pricing Analytics coming soon...</p>
        </div>
      )}

      {/* Modals */}
      <CreateCouponModal />
    </div>
  );
};

export default PricingControls;